// Quick Guest Access Button Component
"use client";

import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useHasMounted } from '@/lib/utils/clientOnly';
import { t } from '@/lib/i18n';
import { UserIcon } from 'lucide-react';
import { useState } from 'react';

interface GuestAccessButtonProps {
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function GuestAccessButton({ 
  className = '', 
  variant = 'outline',
  size = 'default' 
}: GuestAccessButtonProps) {
  const { loginAsGuest, isLoggingIn, isAuthenticated, authInitialized } = useAuth();
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const hasMounted = useHasMounted();

  const handleGuestAccess = async () => {
    if (isLoggingIn || isGuestLoading) return;
    
    try {
      setIsGuestLoading(true);
      console.log('👤 Quick guest access initiated');
      await loginAsGuest();
    } catch (error) {
      console.error('❌ Guest access failed:', error);
    } finally {
      setIsGuestLoading(false);
    }
  };

  const isLoading = isLoggingIn || isGuestLoading;

  // Don't render until mounted, auth initialized, and not authenticated
  if (!hasMounted || !authInitialized || isAuthenticated) {
    return null;
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleGuestAccess}
      disabled={isLoading}
      className={`w-full ${className}`}
    >
      {isLoading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
          {t('actions.guest_accessing')}
        </>
      ) : (
        <>
          <UserIcon className="w-4 h-4 mr-2" />
          {t('actions.continue_as_guest')}
        </>
      )}
    </Button>
  );
} 